import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { Icono } from '../layout/iconos';
import { TODAS } from '../layout/secciones';

/**
 * Piezas base del sistema de diseño.
 *
 * Sobrio y denso a propósito (D9): un mostrador mira la misma pantalla ocho horas, y cada
 * píxel de aire de más es una fila menos a la vista. El color se reserva para el estado
 * —lo que está bien, lo pendiente, lo que salió mal— y no se gasta en decoración.
 *
 * Lo que estas piezas agregaron después: **la jerarquía la da la escala, no el color**. El color
 * ya tiene un trabajo —decir el estado—, así que si además dirigiera la mirada haría dos cosas a
 * la vez y ninguna bien. Un título es más grande y más apretado; una etiqueta es más chica y más
 * tenue; y una cifra que se mira de lejos es una cifra.
 */

type Tono = 'principal' | 'secundario' | 'peligro' | 'sutil';

const TONOS: Record<Tono, string> = {
  principal:
    'bg-acento text-white hover:bg-acento-fuerte disabled:opacity-40 border border-transparent shadow-sm',
  secundario:
    'bg-superficie border border-borde text-texto hover:border-acento hover:text-acento disabled:opacity-40',
  peligro:
    'bg-superficie border border-mal text-mal hover:bg-mal hover:text-white disabled:opacity-40',
  sutil:
    'border border-transparent text-texto-tenue hover:bg-acento-suave hover:text-texto disabled:opacity-40',
};

interface BotonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly tono?: Tono;
  readonly cargando?: boolean;
}

export function Boton({
  tono = 'secundario',
  cargando = false,
  className = '',
  disabled,
  children,
  ...resto
}: BotonProps): React.ReactElement {
  return (
    <button
      // El tipo se declara siempre: un botón sin tipo dentro de un formulario lo envía,
      // y eso convierte un "cancelar" en un alta accidental.
      type="button"
      disabled={disabled === true || cargando}
      aria-busy={cargando}
      className={`inline-flex items-center justify-center gap-1.5 rounded-marco px-3 py-1.5 text-cuerpo font-medium transition-colors disabled:cursor-not-allowed ${TONOS[tono]} ${className}`}
      {...resto}
    >
      {children}
    </button>
  );
}

type TonoDeInsignia = 'neutro' | 'bien' | 'atencion' | 'mal';

/**
 * **Un solo vocabulario para los estados.**
 *
 * Antes cada tono se resolvía distinto —uno con fondo, tres con borde— y el resultado es que
 * «Activa» y «Vencido» no se parecían en nada aunque los dos fueran el mismo tipo de dato. Ahora
 * los cuatro son fondo suave con texto del color: se distinguen entre sí y se reconocen como
 * familia.
 */
const TONOS_DE_INSIGNIA: Record<TonoDeInsignia, string> = {
  neutro: 'bg-acento-suave text-texto-tenue',
  bien: 'bg-bien-suave text-bien',
  atencion: 'bg-atencion-suave text-atencion',
  mal: 'bg-mal-suave text-mal',
};

export function Insignia({
  tono = 'neutro',
  children,
}: {
  readonly tono?: TonoDeInsignia;
  readonly children: ReactNode;
}): React.ReactElement {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-apoyo font-medium ${TONOS_DE_INSIGNIA[tono]}`}
    >
      {children}
    </span>
  );
}

export function Tarjeta({
  titulo,
  descripcion,
  acciones,
  children,
}: {
  readonly titulo?: ReactNode;
  readonly descripcion?: ReactNode;
  readonly acciones?: ReactNode;
  readonly children: ReactNode;
}): React.ReactElement {
  return (
    // **Sombra y aire, no marco.** Una pantalla de ocho tarjetas pegadas con una línea entre ellas
    // se lee como una planilla continua; separadas y con una sombra suave se leen como bloques, que
    // es como alguien las busca. El margen inferior va acá y no en cada pantalla porque depender de
    // que treinta se acuerden es depender de que ninguna se olvide.
    <section className="mb-4 rounded-marco border border-borde bg-superficie shadow-[0_1px_3px_rgb(0_0_0/0.06),0_1px_2px_rgb(0_0_0/0.04)] last:mb-0">
      {titulo === undefined ? null : (
        <header className="flex items-start justify-between gap-4 border-b border-borde-tenue px-4 py-3">
          <div>
            <h2 className="text-seccion font-semibold tracking-tight">{titulo}</h2>
            {descripcion === undefined ? null : (
              <p className="mt-0.5 text-apoyo text-texto-tenue">{descripcion}</p>
            )}
          </div>
          {acciones === undefined ? null : <div className="flex gap-2">{acciones}</div>}
        </header>
      )}
      <div className="p-4 sm:p-5">{children}</div>
    </section>
  );
}

export function Encabezado({
  titulo,
  descripcion,
  acciones,
}: {
  readonly titulo: ReactNode;
  readonly descripcion?: ReactNode;
  readonly acciones?: ReactNode;
}): React.ReactElement {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div className="flex items-center gap-3">
        {/* El mismo ícono que el menú, en grande.
            **No decora: orienta.** Es el ancla que dice «estás acá» sin obligar a leer el título ni
            a buscar cuál de las treinta entradas del menú quedó resaltada. Por eso repite el del
            menú y no elige uno más lindo: si fueran distintos habría que aprender dos. */}
        <IconoDeLaPantalla />
        <div>
          <h1 className="text-titulo font-semibold">{titulo}</h1>
          {descripcion === undefined ? null : (
            <p className="mt-1 text-cuerpo text-texto-tenue">{descripcion}</p>
          )}
        </div>
      </div>
      {acciones === undefined ? null : <div className="flex gap-2">{acciones}</div>}
    </div>
  );
}

/**
 * El ícono de la pantalla en la que se está, deducido de la ruta.
 *
 * Se deduce y no se pasa por parámetro: treinta pantallas tendrían que declararlo, y la que se
 * olvidara quedaría distinta sin que nadie lo note. La lista de secciones ya sabe qué ícono le
 * toca a cada ruta, y esa lista tiene una prueba que la obliga a estar completa.
 */
function IconoDeLaPantalla(): React.ReactElement | null {
  const ruta = useRouterState({ select: (estado) => estado.location.pathname });
  const seccion =
    TODAS.find((posible) => posible.ruta !== '/' && ruta.startsWith(posible.ruta)) ??
    TODAS.find((posible) => posible.ruta === ruta);
  if (seccion === undefined) return null;

  return (
    <span className="hidden size-9 shrink-0 place-items-center rounded-marco bg-acento-suave text-acento sm:grid">
      <Icono nombre={seccion.icono} className="size-5" />
    </span>
  );
}
